import { TIME_SERIES_CHART_TYPES, LEGEND_TYPES, WrapperWidgetUIProps, TimeSeriesData } from '@carto/meridian-ds/widgets'
import { SourceFilters, SpatialFilter } from '@carto/react-redux'
import { QueryParameters, TilejsonResult } from '@deck.gl/carto'
import { Color } from '@deck.gl/core'
import { Position } from '@deck.gl-community/editable-layers'

import { AGGREGATION_TYPES, ALL_FIELD_TYPES } from '@carto/kepler.gl/constants'
import { LayerGroup, RGBAColor, RGBColor } from '@carto/kepler.gl/reducers'
import { Layer, LayerBaseConfig } from '@carto/kepler.gl/layers'
import {
  AggregationType,
  CategoryOrderBy,
  CategoryResponse,
  GroupDateType,
  RasterMetadata,
  TimeSeriesResponse
} from '@carto/api-client'

import { ProviderType } from 'features/connections/types'
import { MapType, Map, MapLockStatus, SqlParameter, MapDataset, Formats } from 'features/maps/types'
import { Client } from 'features/common/utils/clientParameter'
import { SchemaField } from 'features/data-explorer/types'
export { MapRightPaneTab as RightPaneTab } from 'features/maps/types'

export type ValueOf<T> = T[keyof T]

export enum DistanceUnits {
  Kilometers = 'kilometers',
  Miles = 'miles'
}

export enum MinimizedDistanceUnits {
  Kilometers = 'km',
  Miles = 'mi',
  Meters = 'm',
  Feet = 'ft'
}

export enum BasemapProvider {
  CARTO = 'carto',
  Google = 'google',
  Amazon = 'amazon', // deprecated
  Custom = 'custom'
}

export enum MapStyleType {
  /**
   * Carto stock style (rendered by Maplibre)
   * NOTE: CARTO_MAPLIBRE would be more accurate
   */
  MAPLIBRE = 'mapLibre', // default, not required

  /**
   * Google Maps style (rendered by Google Maps API)
   */
  GOOGLE_MAPS = 'gmaps',

  /**
   * User supplied style (rendered by Maplibre)
   * NOTE: so CUSTOM_MAPLIBRE would be more accurate
   */
  CUSTOM = 'custom',

  /** @deprecated Kept for documentation of removed features */
  AMAZON = 'amazon'
}

export enum GoogleMapsMapTypeId {
  ROADMAP = 'roadmap',
  SATELLITE = 'satellite',
  HYBRID = 'hybrid',
  TERRAIN = 'terrain'
}

export type GoogleMapStyle = {
  type: MapStyleType.GOOGLE_MAPS
  options: {
    mapId?: string
    mapTypeId?: GoogleMapsMapTypeId
  }
}

export interface BuilderBasemapStyle {
  // inherited from KeplerMapStyle model
  type: MapStyleType
  id: string
  label: string
  icon?: string
  layerGroups?: LayerGroup[]
  url?: string

  // Actual style is react-map/malibre MapStyle | GoogleMapStyle, but it's impossible
  // to use it as it causes
  //    Type instantiation is excessively deep and possibly infinite
  // when trying to instantiate type for reducers
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: any // MapStyle | GoogleMapStyle

  // carto internal
  failedToLoad?: boolean
  disabled?: boolean
  customAttribution?: string
  originalUrl?: string
}

export enum BuilderMode {
  Public = 'Public',
  Viewer = 'Viewer',
  Editor = 'Editor'
}

export type KeplerFieldType = ValueOf<typeof ALL_FIELD_TYPES>

export type KeplerLayerTextLabel = LayerBaseConfig['textLabel']['0'] // LayerTextLabel
export type RealKeplerTableField_ = NonNullable<KeplerLayerTextLabel['field']> // Field

export type KeplerTableField = RealKeplerTableField_ & {
  id: string
  /** Type as in Keplers ALL_FIELD_TYPES  */
  type: KeplerFieldType

  // Carto extensions
  selectedAggregator?: string
  artificial?: boolean

  // Only if hex color is used, column selected to get HEX colors.
  colorColumn?: string
  colorColumnIdx?: number
}

export enum GeometryType {
  Point = 'Point',
  MultiPoint = 'MultiPoint',
  Line = 'Line',
  LineString = 'LineString',
  MultiLineString = 'MultiLineString',
  Polygon = 'Polygon',
  MultiPolygon = 'MultiPolygon',
  Unknown = 'Unknown'
}

export enum RasterStyleType {
  Rgb = 'Rgb',
  ColorRange = 'ColorRange',
  UniqueValues = 'UniqueValues'
}

export type ColorBand = 'red' | 'green' | 'blue' | 'alpha'

export type RasterLayerConfigColorBand = {
  band: ColorBand
  type: 'none' | 'band' | 'expression'
  value: string // band name or expression
}

export enum KeplerScaleType {
  Linear = 'linear',
  Quantize = 'quantize',
  Quantile = 'quantile',
  Ordinal = 'ordinal',

  /**
   * Custom scale - requres `colormap` defined in related colorRange.
   *
   * In Builder, `Logarithmic scale` is _discrete_ and so implemented as `custom`
   * with `uiCustomScaleType` indicator set to `logarithmic` in `colorRange.
   */
  Custom = 'custom',
  Identity = 'identity'
}

export const LayerTypes = {
  // placeholder layer that renders nothing, but allows to specify spatial data settings
  Unknown: 'unknown' as const,

  // old document mode types
  Point: 'point' as const,
  GeoJSON: 'geojson' as const,
  Line: 'line' as const,
  S2: 's2' as const,
  HexagonId: 'hexagonId' as const, // h3 column in data
  Grid: 'grid' as const, // points visualized locally as quad grid
  Hexagon: 'hexagon' as const, // points visualized locally as hexagon grid,
  Heatmap: 'heatmap' as const,
  Cluster: 'cluster' as const,
  Trip: 'trip' as const,

  // tile-based layers
  Tileset: 'tileset' as const,
  Quadbin: 'quadbin' as const,
  H3: 'h3' as const,
  HeatmapTile: 'heatmapTile' as const,
  ClusterTile: 'clusterTile' as const,
  Raster: 'raster' as const
} as const

export type LayerType =
  | typeof LayerTypes.Unknown
  | typeof LayerTypes.Point
  | typeof LayerTypes.GeoJSON
  | typeof LayerTypes.Line
  | typeof LayerTypes.S2
  | typeof LayerTypes.HexagonId
  | typeof LayerTypes.Grid
  | typeof LayerTypes.Hexagon
  | typeof LayerTypes.Heatmap
  | typeof LayerTypes.Cluster
  | typeof LayerTypes.Trip
  | typeof LayerTypes.Tileset
  | typeof LayerTypes.Quadbin
  | typeof LayerTypes.H3
  | typeof LayerTypes.HeatmapTile
  | typeof LayerTypes.ClusterTile
  | typeof LayerTypes.Raster

export type SchemaColumn = {
  name: string
  type: string
}

export type KeplerTableMetadata = {
  tilejson?: TilejsonResult | null

  /**
   * Known values:
   *  * `tileset` for most _tile_ layers, even if it's `query` or `table`
   *  * `raster` for `raster` layers
   *  * `table` for deprecated document mode layers
   */
  type: MapType

  /** Same as `MapDataset.type` for tile layers. */
  sourceType?: MapType

  schema?: SchemaField[]

  /** Copied from `MapDataset.aggregationExp` */
  aggregationExp?: string | null

  hexColumnStats?: Record<string, AttributeStats>
  raster_metadata?: RasterMetadata
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any
}

export type KeplerTable = {
  id: string

  /// Color used to represent the dataset in the UI
  color: number[]

  metadata: KeplerTableMetadata
  fields: KeplerTableField[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allData: { [x: string]: any }[]
  allIndexes: number[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataContainer?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any
  // Label derived (same as MapDataset.label)
  label: string

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gpuFilter?: any

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterRecord?: any
}

export type KeplerVisualChannel<T = unknown> = {
  key: string

  /** Name fo property in `visConfig` if `layer.config[field]` is empty. */
  property: string

  /** name of property in `layerConfig` containing selected field */
  field: string
  range: string
  domain: string
  scale: string
  channelScaleType: string
  nullValue: T

  /** Name of aggregationproperty in KeplerLayerVisConfig */
  aggregation?: string | null
  accessor?: string
  supportedFieldTypes?: string[]

  condition?: (layerConfig: KeplerLayerConfig) => boolean

  getAttributeValue?: (config: KeplerLayerConfig) => T
  // defaultValue must be static as it's used in updateTriggers
  defaultValue?: T | ((layerConfig: KeplerLayerConfig) => T)

  // added by Carto
  opacityProperty?: string
}

export type KeplerLayerVisConfigSetting = {
  type: string /// known values: 'number', 'boolean', 'color-range-select', 'color-select'
  isRanged?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue: any // known values: 1, [1,2]
  range?: [number, number]
  step?: number
  property: string // name of property in KeplerLayerVisConfig
}

export type KeplerColorMapNumeric = [number | null, string][]
export type KeplerColorMapString = [string | null, string][]

export type KeplerColorMap = [string | number | null, string][]

export type KeplerColorRange = {
  name: string
  category: string
  type: string // sequential, qualitative, etc

  // colors as hex strings
  colors: string[]
  // array, where first element is value second is target color
  // filled only for custom scales
  colorMap?: KeplerColorMap

  // Carto additions

  // If color range scale is custom, then it may be actually "logarithmic" one which is
  // implemented as "custom", but it's not custom at all
  uiCustomScaleType?: string

  // If this color range is _driven_ by Hex Color Column
  hexColor?: boolean
}

// TODO: Disambiguate AGGREGATION_TYPES from `@carto/kepler.gl` from
// AggregationTypes from `@carto/react-core`.
export type KeplerAggregationType = ValueOf<typeof AGGREGATION_TYPES>

export type KeplerLayerVisConfig = {
  filled?: boolean
  colorAggregation?: KeplerAggregationType
  colorRange?: KeplerColorRange

  stroked?: boolean
  strokeColorAggregation?: KeplerAggregationType
  strokeColorRange?: KeplerColorRange

  sizeAggregation?: KeplerAggregationType
  heightAggregation?: KeplerAggregationType

  customMarkers?: boolean
  customMarkersUrl?: string
  customMarkersId?: string
  customMarkersRange?: CustomMarkersRange | null
  radius?: number
  radiusRange?: [number, number]

  enable3d?: boolean

  rasterStyleType?: RasterStyleType
  colorBands?: RasterLayerConfigColorBand[]
  uniqueValuesColorRange?: KeplerColorRange

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [name: string]: any
}

export type KeplerLayerConfig = {
  dataId: string
  label: string
  type: string
  isVisible: boolean
  isConfigActive: boolean
  highlightColor: RGBColor | RGBAColor
  hidden: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Record<string, any>
  color: number[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  animation?: any

  colorField?: KeplerTableField
  colorScale?: KeplerScaleType
  colorDomain?: Array<string | number | null>
  strokeColorField?: KeplerTableField
  sizeField?: KeplerTableField // this is used for lineWidgth / strokeWidth
  radiusField?: KeplerTableField
  customMarkersField?: KeplerTableField
  rotationField?: KeplerTableField
  heightField?: KeplerTableField
  weightField?: KeplerTableField

  uniqueValuesColorScale?: KeplerScaleType
  uniqueValuesColorField?: KeplerTableField
  uniqueValuesColorDomain: (number | null)[]

  textLabel: KeplerLayerTextLabel[]

  visConfig: KeplerLayerVisConfig
  visibilityByZoom?: {
    min: number
    max: number
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  colorUI: Record<string, any>

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [name: string]: any
}

export type KeplerLayerMeta = {
  bounds: [number, number, number, number]
  featureTypes?: { point?: boolean; polygon?: boolean; line?: boolean; linestring?: boolean }
  // Properties below are marked as 'required', to be stricter in most cases.
  // However, in 'document mode' (planned for removal) they may be undefined.
  formatTiles: string
  geoColumn: string
  scheme: string
  sourceType: MapType
  dataResolution?: number
  provider: ProviderType
  rasterMetadata?: RasterMetadata
}

// TODO(types): Consider renaming as CartoKeplerLayer or BuilderLayer.
// All layers with legends are KeplerLayers, once document mode is removed.
export type KeplerLayer = Layer & {
  id: string
  type: LayerType
  meta: KeplerLayerMeta
  readonly name: string
  readonly layerIcon: React.ComponentType

  readonly requireData: boolean
  readonly config: KeplerLayerConfig
  readonly visualChannels: Record<string, KeplerVisualChannel>
  readonly visConfigSettings: Record<string, KeplerLayerVisConfigSetting>
  readonly isLoadingTiles: boolean

  // Carto fields
  legendSettings: HexColorLegendSettings
  readonly canDropFeatures: boolean

  updateLayerConfig(newConfig: Partial<KeplerLayerConfig>): void
  validateScale(channelKey: string): void
  getScaleOptions(channelKey: string): string[]

  // Carto methods
  getNewLayerConfig?: (dataset: KeplerTable, layers: KeplerLayer[]) => Partial<KeplerLayerConfig>
  isColoredByColorColumn(rangeFieldName: string): boolean
  updateColorMap(rangeFieldName: string, colorMap: KeplerColorMap, colorRange: KeplerColorRange): KeplerColorRange
}

// Kepler serializes visual channels in different way that it keeps in state
export type KeplerStoredVisualChannel = {
  name: string
  type: string

  channelScaleType?: string
  colorColumn?: string
}

/**
 * Kepler serializes config in different way that it keeps in state
 *  - xxxFields are stored in `visualChannels` as only name and type
 *  - xxxScale are stored in `visualChannels` too
 */
export type KeplerStoredLayer = {
  id: string
  type: LayerType
  config: KeplerLayerConfig
  visualChannels: Record<string, KeplerStoredVisualChannel | string | undefined | null>
}

export type FindDefaultLayerResult = {
  props: Partial<KeplerLayerConfig>[]
}

export type KeplerLayerClass = {
  new (props?: KeplerLayerConfig): KeplerLayer
  type: string

  findDefaultLayerProps(dataset: KeplerTable, previous): FindDefaultLayerResult
}

export type KeplerLayerClasses = Partial<Record<LayerType, KeplerLayerClass>>

// this is same as "serialized" KeplerLayer or `MapConfigLayer` from carto/api-client
// TODO: consider moving it somewhere when learning more about it
export type BuilderLayer = {
  id: string
  type: LayerType
  config: KeplerLayerConfig
  visualChannels: Partial<Record<string, KeplerStoredVisualChannel | string | null>>

  /**
   * This field contains last known legend colors for channels colored by "hex color" feature.
   * It's used only for legends.
   */
  legendSettings: HexColorLegendSettings
}

// and this is builder layer with data loaded
export type BuilderLayerWithData = BuilderLayer & {
  mapDataset: MapDataset
  data?: TilejsonResult
}

export type BuilderDatasetInternalFields = {
  letter: string
  color: string // mapDataset has optional color, now it's always defined

  tilejson?: TilejsonResult | null
  schema?: SchemaField[]
  metadata?: KeplerTableMetadata
}

export type BuilderDataset = MapDataset & BuilderDatasetInternalFields
export type BuilderDatasetEnriched = BuilderDataset & KeplerTable

export type MapDatasetEnriched = MapDataset & KeplerTable

export interface CartoLayerOpts {
  datasetId?: string
  provider: ProviderType
  type: MapType
  source: string
  format?: Formats
  geoColumn?: string | null
  columns?: string[] | null
  connection?: string
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  queryParameters?: QueryParameters | null
  queryTemplate?: string | null
  filters?: SourceFilters
}

export interface LayerRequest extends CartoLayerOpts {
  mapId: string
  accessToken?: string
  fromAnalysis?: boolean
  minimized?: boolean
  sqlParameters?: SqlParameter[]
  createLayer?: boolean
}

export type InitBuilderRequestParams = {
  builderMode: BuilderMode
  mapId?: string
  createMapRequest?: CreateMapRequest
  searchParams?: Record<string, string>
}

export interface CreateMapRequest {
  map: Omit<Map, 'id'>
  layer?: CartoLayerOpts
}

export interface FetchLayerDataProps extends CartoLayerOpts {
  client?: Client
  headers?: Record<string, string>
  accessToken?: string
  expectErrorWithSchema?: boolean
  mapId: string
}

export interface MapCredentials {
  accessToken: string
  apiBaseUrl: string
}

export interface MeasureTooltip {
  position: Position
  distance: number
}

export const BUILDER_MEASURE_TOOL_UNIT_KEY = 'builder-measureToolUnit'
export const BUILDER_MOBILE_MODAL_DONOTSHOW_KEY = 'BUILDER_MOBILE_MODAL_DONOTSHOW_KEY'

export enum WidgetType {
  CATEGORY = 'category',
  FORMULA = 'formula',
  HISTOGRAM = 'histogram',
  TIMESERIES = 'timeseries',
  TABLE = 'table',
  RANGE = 'range',
  PIE = 'pie'
}

export type WidgetState<T> =
  | {
      state: WidgetStateType.Loading
    }
  | {
      state: WidgetStateType.Success
      data: T
    }
  | {
      state: WidgetStateType.Error
      error?: string
      warning?: string
    }

export enum WidgetStateType {
  Loading = 'loading',
  Success = 'success',
  Error = 'error'
}

export enum NoteFormat {
  PlainText = 'plainText',
  Markdown = 'markdown'
}

export enum BuilderWidgetPlacement {
  RightPane = 'rightPane',
  BottomPane = 'bottomPane'
}

export interface BaseWidget {
  /** ID for the widget instance. */
  id: string
  /** Title to show in the widget header. */
  title: string
  /** ID of the data source to get the data from. */
  dataSource: string
  /** Name of primary dataset column for widget. */
  column?: string
  operation?: AggregationType
  spatialIndexAggregation?: KeplerAggregationType
  formatter?: string
  note?: string
  noteFormat?: NoteFormat
  isValid: boolean
  wrapperProps?: Partial<WrapperWidgetUIProps>
  collapsible?: boolean
  autoCollapse?: boolean
  global?: boolean
  noteExpanded?: boolean
  state?: WidgetState<unknown>
}

export interface FilterableWidget {
  disableFiltering?: boolean
  crossFilteringDataSourceIds?: string[]
}

export interface FormulaWidget extends BaseWidget {
  type: WidgetType.FORMULA
  operationExp?: string
  state?: WidgetState<number>
}

export interface CategoryWidget extends BaseWidget, FilterableWidget {
  type: WidgetType.CATEGORY
  operationColumn: string
  operationExp?: string
  orderBy?: CategoryOrderBy
  spatialIndexAggregationOperationColumn?: KeplerAggregationType
  state?: WidgetState<CategoryResponse>
}

export interface HistogramWidget extends BaseWidget, FilterableWidget {
  type: WidgetType.HISTOGRAM
  buckets: number
  xAxisFormatter?: string
  min?: number
  max?: number
  state?: WidgetState<{
    min: number
    max: number
    ticks: number[]
    values: number[]
  }>
}

export type TimeSeriesWidgetSerie = {
  operation: AggregationType
  operationColumn: string
  operationExp?: string
  customMetricLabel?: string
}

export type TimeSeriesDataAndCategories = TimeSeriesResponse & {
  rows: TimeSeriesData[]
  categories?: string[]
  options?: {
    splitByCategory?: boolean
    series?: TimeSeriesWidgetSerie[]
    operation?: AggregationType
  }
}

export interface TimeSeriesWidget extends BaseWidget, FilterableWidget {
  type: WidgetType.TIMESERIES
  operationColumn: string
  operationExp?: string
  customMetricLabel?: string
  /** Step applied to group the data. */
  stepSize: GroupDateType
  /** Multiplier applied to `stepSize`. Use to aggregate by 2 hours, 5 seconds, etc. */
  stepMultiplier?: number
  /** Chart used to represent the time series. */
  chartType: TIME_SERIES_CHART_TYPES
  /** Enable/disable animation controls (play, pause, stop, speed). Default: true. */
  showControls: boolean
  /** Name of the data source's column to split the data by category. */
  splitByCategory?: string
  /** Select specific categories. Default: Most 'dominant' categories */
  splitByCategoryValues?: string[]
  /** Limit of categories shown. Default: 5. */
  splitByCategoryLimit?: number
  /** Array of {operation, operationColumn} objects that specify multiple aggregations. */
  series?: TimeSeriesWidgetSerie[]
  /** Height of the chart. Default: 22 x theme.spacingValue. */
  height?: string
  state?: WidgetState<TimeSeriesDataAndCategories>
}

export interface PieWidget extends BaseWidget, FilterableWidget {
  type: WidgetType.PIE
  operationColumn: string
  operationExp?: string
  spatialIndexAggregationOperationColumn?: KeplerAggregationType
  state?: WidgetState<CategoryResponse>
}

export enum TableColumnType {
  Number = 'number',
  String = 'string',
  Date = 'date'
}

export interface TableColumn {
  field: string
  headerName?: string
  format?: string
  type?: TableColumnType
  spatialIndexAggregation?: KeplerAggregationType
}

type RowGeometry =
  | {
      type: 'Point'
      coordinates: [number, number]
    }
  | {
      type: 'LineString'
      coordinates: [number, number][]
    }
  | {
      type: 'Polygon'
      coordinates: [number, number][][]
    }

export type TableWidgetRow = {
  geoid: number
  name: string
} & {
  [key: string]: RowGeometry | string | bigint | number
}

export type LayerExtent = {
  xmin: number
  ymin: number
  xmax: number
  ymax: number
}

export interface TableWidget extends BaseWidget {
  type: WidgetType.TABLE
  columns: TableColumn[]
  dense?: boolean
  pageSize?: number
  // Omit 'data'; Table Widget may contain Quadbin values (BigInt) that are not serializable by Redux.
  state?: WidgetState<unknown>
}

export interface RangeWidget extends BaseWidget, FilterableWidget {
  type: WidgetType.RANGE
  min?: number
  max?: number
  state?: WidgetState<{ min: number; max: number; values: { min: number; max: number } }>
}

export type Widget =
  | CategoryWidget
  | FormulaWidget
  | HistogramWidget
  | RangeWidget
  | TimeSeriesWidget
  | TableWidget
  | PieWidget

export type WidgetsMapConfig = {
  widgets: Widget[]
  filters: Record<string, SourceFilters>
  spatialFilter: SpatialFilter | null
}

export enum MapViewerType {
  public,
  shared
}

export interface FormattedLabel {
  value: number
  label: string
}

interface SplitMap {
  layers: { [layerId: string]: boolean }
}

export type SplitMaps = SplitMap[]

export interface LegendWidgetProps {
  type: LEGEND_TYPES
  basedOn?: string
  basedOnLabelId?: string
  colors?: string[] | Color[]
  labels?: string[] | FormattedLabel[]
  isStrokeColor?: boolean
  isCustomMarkers?: boolean // TODO: maybe some proper enum?
  truncated?: number
  customMarkers?: string | string[]
  fallbackMarker?: string
  maskedMarkers?: boolean
}

export interface LegendEntry {
  widgetProps: LegendWidgetProps
  visualChannel: string
  hash?: string
}

export interface LayerLegendEntrySettings {
  visualChannel: string
  customFieldLabel?: string
  customColorLabels?: string[]
  hash?: string
}

export interface LayerLegendSettings {
  /**
   * If active, a layer is shown in the layer list panel (optionally with legends),
   * and enabled for exports. If inactive, the layer and all legend entries are
   * hidden and the layer is excluded from exports.
   *
   * Prior to March 2025, this property was used only to show/hide legend
   * entries, but not to remove the layer from the layer list and exports. Map
   * configs have since been migrated, and the old behavior is now described by
   * the `shouldDisplayEntries` property below.
   */
  active: boolean
  /**
   * Whether or not a layer's legend entries are shown.
   *
   * Prior to March 2025, this behavior was described by the `active` property above.
   */
  shouldDisplayEntries: boolean
  entries?: LayerLegendEntrySettings[]
}

export const DEFAULT_MAP_INDEX = 0
export const SPLIT_MAP_INDEX = 1
export type MapIndex = typeof DEFAULT_MAP_INDEX | typeof SPLIT_MAP_INDEX

export enum LegendSettingsVersion {
  // v0, active means "legend is not visible for this layer", but _layer_ is on list
  V0 = 'v0',

  // v1, active means hide/show legend for this layer and if false, it's completly unavailable also for export
  // v1, shouldDisplayEntries means what "active" means in v0
  V1 = 'v1'
}

export interface LegendSettings {
  initialExpanded?: boolean // Some Maps published without the `expanded` property might have this, so we need to keep it for backwards compatibility
  expanded: { [DEFAULT_MAP_INDEX]: boolean; [SPLIT_MAP_INDEX]?: boolean }
  baseMapSelector?: boolean
  layers: Record<string, LayerLegendSettings>
  version?: LegendSettingsVersion
}

export type HexColorMapEntry = { label: string; color: string }
export type HexColorLegendSettings = Record<string, HexColorMapEntry[]>

export type NoSpatialIndexType = 'geom'
export const NoSpatialIndex: NoSpatialIndexType = 'geom'

export enum TilingScheme {
  Xyz = 'xyz',
  H3 = 'h3',
  Quadbin = 'quadbin'
}

export enum SpatialDataType {
  Geo = 'geo',
  H3 = 'h3',
  Quadbin = 'quadbin'
}

export enum SpatialIndex {
  H3 = 'h3',
  QUADBIN = 'quadbin'
}

export type SpatialSelectorType = SpatialIndex | NoSpatialIndexType

export const SpatialIndexColumn = Object.freeze({
  [SpatialIndex.H3]: ['h3', 'hex', 'h3id', 'hex_id', 'h3hex'],
  [SpatialIndex.QUADBIN]: ['quadbin']
})

export interface AttributeStatsRequest {
  datasetId: string
  attribute: string
  maxNumberOfCategories?: number
  noCache?: boolean
  cacheMaxAgeInMinutes?: number
}

export enum AttributeType {
  String = 'String',
  Number = 'Number',
  Timestamp = 'Timestamp',
  Boolean = 'Boolean'
}

export type AttributeStatsBase = {
  attribute: string
  type: AttributeType
}

export type AttributeStatsNumber = AttributeStatsBase & {
  type: AttributeType.Number
  min: number
  avg: number
  max: number
  sum: number
  quantiles: number[][]
}

export type AttributeStatsTimestamp = AttributeStatsBase & {
  type: AttributeType.Timestamp
  min: string
  max: string
}

export interface AttributeStatsStringCategory {
  category: string
  frequency: number
}

export type AttributeStatsString = AttributeStatsBase & {
  type: AttributeType.String | AttributeType.Boolean
  categories: AttributeStatsStringCategory[]
}

/**
 * Result of getAttributeStats request to backend.
 */
export type AttributeStats = AttributeStatsString | AttributeStatsNumber | AttributeStatsTimestamp

export enum ShareDialogTabs {
  ShareSettings = 'ShareSettings',
  MapSettings = 'MapSettings'
}

export enum ColorPalette {
  Picker = 'picker',
  Defined = 'defined'
}

export enum PopupEvent {
  Hover = 'hover',
  Click = 'click'
}

export enum PopupStyle {
  Light = 'light',
  LightWithHiFirst = 'lightWithHiFirst',
  Dark = 'dark',
  DarkWithHiFirst = 'darkWithHiFirst',
  Panel = 'panel',
  None = 'none'
}

export interface PopupField {
  name: string
  customName?: string
  isExpression?: boolean
  format?: string
  spatialIndexAggregation?: KeplerAggregationType
}

export interface PopupFormatSettings {
  fields: PopupField[]
  style: PopupStyle
  templateMode?: boolean
  template?: string
  templateEdited?: boolean
}

export interface LayerPopupSettings {
  enabled?: boolean
  [PopupEvent.Hover]?: PopupFormatSettings
  [PopupEvent.Click]?: PopupFormatSettings
}

export interface PopupSettings {
  layers: Record<string, LayerPopupSettings>
  coordinates?: boolean
  comparison?: string
}

export type PopupValue = {
  url?: string
  asImg?: boolean
  displayValue: string | null
  displayDeltaValue: string | null
}

export type PopupRow = {
  field: PopupField
  value: PopupValue
}

export type FeatureDetails = {
  lngLat: number[]
  layerId: string
  rows: PopupRow[]
  mapViewId: number
}

export type CustomMarkerEntry = {
  value: string | null
  markerUrl?: string
  markerId?: string
}

export type CustomMarkersRange = {
  othersMarkerId?: string
  othersMarker?: string
  markerMap: CustomMarkerEntry[]
}

export enum NamedQueryParameters {
  From = 'start',
  To = 'end'
}

export interface DatesFilter {
  [NamedQueryParameters.From]: string | null
  [NamedQueryParameters.To]: string | null
  temporalColumn: string
}

// If in the future we add more filter, for example CategoryFilter change this
// type to Partial<DatesFilter & CategoryFilter>
export type QueryFilters = Partial<DatesFilter>

export type BuilderLockState = MapLockStatus & {
  takingOver?: boolean
  unlockedAt?: number
}

export type TakeOverResponseState = {
  active: boolean
  accepted?: boolean
  timedOut?: boolean
  userId?: string
  email?: string
  requestedTime?: number
}

export enum DatasetItemAction {
  AddLayer = 'addLayer',
  StartAnalysis = 'analysis',
  ShowTable = 'showTable',
  OpenSql = 'openSql',
  Delete = 'delete',
  QueryThisTable = 'queryThisTable',
  Duplicate = 'duplicate',
  Rename = 'rename',
  RefreshSource = 'refresh-source',
  SwapDataSource = 'swapDataSource',
  ShowInWorkflows = 'showInWorkflows'
}

export type SplitMapPanelSide = 'top' | 'bottom' | 'left' | 'right'

export enum RightPaneType {
  Controls = 'controls',
  Description = 'description'
}

export interface SQLPanelProps {
  mapId: string
  // It's undefined if the panel is not still attached to a dataset
  datasetId?: string
  provider?: ProviderType
  connection?: string
  query?: string
  fromAnalysis?: boolean
  minimized?: boolean
  key?: string
}

export interface AISettings {
  enabled: boolean
  mapContext: string
  conversationStarters: string[]
  userGuideEnabled: boolean
  userGuide: string
  /** After migrating {@link AISettings} to {@link Agent}, migration must not run again. [sc-505727] */
  migratedAt?: string
}

export type DatasetConfigForAgent = {
  id: string
  source: string
  label: string
  connectionName: string
  provider: ProviderType
  type: MapType
  schema: SchemaColumn[]
  geoColumn?: string
}

export type MapConfigForAgent = {
  title: string
  description: string
  splitMap: boolean
  layerVisibilityBySplitMap?: {
    leftMap: Array<{ layerId: string; isVisible: boolean }>
    rightMap: Array<{ layerId: string; isVisible: boolean }>
  }
  mapState: {
    latitude: number
    longitude: number
    zoom: number
  }
  connections: Array<{ name: string; provider_id: ProviderType }>
  datasets: Array<DatasetConfigForAgent>
  layers: Array<{
    id: string
    type: string
    label: string
    dataId: string
    isVisible: boolean
    legend?: Array<{
      property: string
      basedOn?: string
      colors?: string[]
      labels?: string[]
      icons?: string[]
    }>
  }>
  widgets: Array<{
    id: string
    label: string
    note?: string
    dataSource: string
    type: WidgetType
    column?: string
    operation?: AggregationType
    operationColumn?: string
    global: boolean
    viewport: boolean
  }>
  mapSettings?: {
    'Export PDF'?: boolean
    'Search Address'?: boolean
    'Change Basemap'?: boolean
    'Export CSV'?: boolean
    'Measure Distance'?: boolean
  }
  parameterControls?: SqlParameter[]
}

export enum BuilderGenAIEnvironment {
  Live = 'live',
  Staging = 'staging',
  Unsupported = 'unsupported'
}

export enum AIAPIVersion {
  /** @deprecated */
  V0 = 0,
  V1 = 1
}

export type BuilderGenAIAgent = {
  id: string
  version: string
  apiVersion: AIAPIVersion
}

export type BuilderGenAIThread = {
  parent: string
  /** Associates 'create_source' actions with datasets. */
  datasets: BuilderGenAIDataset[]
  /** Associates 'create_layer' actions with datasets. */
  layers: BuilderGenAILayer[]
}

export enum BuilderGenAIAuthor {
  User = 'User',
  Bot = 'Bot',
  System = 'System'
}

export enum BuilderGenAIResourceState {
  Loading = 'loading',
  Success = 'success',
  Error = 'error'
}

/**
 * Binding from a create_layer {@link BuilderGenAIAction} to the resulting
 * layer ID, with possible loading or error states. Intended to help limit
 * the amount of AI-specific metadata added to stored layer definitions.
 */
export type BuilderGenAILayer = {
  actionId: string
  state: BuilderGenAIResourceState
  layerId?: string
  error?: string
} & (
  | { state: BuilderGenAIResourceState.Loading; layerId?: string; error?: undefined }
  | { state: BuilderGenAIResourceState.Success; layerId: string; error?: undefined }
  | { state: BuilderGenAIResourceState.Error; layerId?: string; error: string }
)

/**
 * Binding from a create_source {@link BuilderGenAIAction} to the resulting
 * dataset ID, with possible loading or error states. Intended to help limit
 * the amount of AI-specific metadata added to stored dataset definitions.
 */
export type BuilderGenAIDataset = {
  actionId: string
  state: BuilderGenAIResourceState
  datasetId?: string
  error?: string
} & (
  | { state: BuilderGenAIResourceState.Loading; datasetId?: string; error?: undefined }
  | { state: BuilderGenAIResourceState.Success; datasetId: string; error?: undefined }
  | { state: BuilderGenAIResourceState.Error; datasetId?: string; error: string }
)

export type BuilderGenAIConversationItem =
  | { author: BuilderGenAIAuthor.User; message: string; date: string }
  | { author: BuilderGenAIAuthor.Bot; type: 'delta'; message: string }
  | { author: BuilderGenAIAuthor.Bot; type: 'response'; parent: string; message: string }
  | { author: BuilderGenAIAuthor.Bot; type: 'action_frontend'; actions: BuilderGenAIAction[] }
  | { author: BuilderGenAIAuthor.Bot; type: 'action_backend'; action: BuilderGenAIBackendActionTypes }
  | { author: BuilderGenAIAuthor.System; message: BuilderGenAISystemMessage }

export type BuilderGenAIConversation = BuilderGenAIConversationItem[]

export type BuilderGenAIResponse =
  | {
      type: 'delta'
      message: string
    }
  | {
      type: 'response'
      parent: string
      message: string
    }
  | {
      type: 'action_frontend'
      actions: BuilderGenAIAction[]
    }
  | {
      type: 'action_backend'
      action: BuilderGenAIBackendActionTypes
    }
  | {
      type: 'error'
      error: BuilderGenAISystemMessage
    }

export interface useGenAIActionProps {
  onResult: (response: BuilderGenAIActionResult) => void
  actionToExecute: BuilderGenAIAction | undefined
}

export enum BuilderGenAISystemMessage {
  MaxThreadsError = 'max_builder_gen_ai_threads',
  MaxTokensError = 'max_builder_gen_ai_tokens',
  UnspecifiedError = 'unspecified'
}

// Actions available to GenAI Agents that will be executed server-side. The
// Agent uses versioned action names (e.g. execute_query_v1_0_0) but for
// frontend purposes the differences do not matter.
export enum BuilderGenAIBackendActionTypes {
  ExecuteQuery = 'execute_query',
  ExecuteQueryAsync = 'execute_query_async'
}

export enum BuilderGenAIActionTypes {
  Focus = 'set_map_center_and_zoom',
  FocusLayer = 'set_map_center_and_zoom_to_layer',
  GetFormulaWidget = 'get_formula_widget',
  GetCategoryWidget = 'get_category_widget',
  GetHistogramWidget = 'get_histogram_widget',
  GetTimeSeriesWidget = 'get_time_series_widget',
  FilterCategoryWidget = 'filter_category_widget',
  FilterHistogramWidget = 'filter_histogram_widget',
  FilterRangeWidget = 'filter_range_widget',
  SetLayersVisibility = 'set_layers_visibility',
  AnalyzeMapViewport = 'analyze_map_viewport',
  GetMapCoordinates = 'get_map_coordinates',
  GetSpatialFilter = 'get_spatial_filter',
  SetSqlParameterText = 'set_sql_parameter_text',
  SetSqlParameterDateRange = 'set_sql_parameter_date_range',
  SetSqlParameterNumeric = 'set_sql_parameter_numeric',
  SetSqlParameterNumericRange = 'set_sql_parameter_numeric_range',
  AddSource = 'add_source',
  RemoveSource = 'remove_source',
  AddLayer = 'add_layer',
  RemoveLayer = 'remove_layer',
  SetLayerStyle = 'set_layer_style'
}

export interface BuilderGenAIAction {
  action: BuilderGenAIActionTypes
  params?: Record<string, unknown>
  id: string
  isCompleted?: boolean
}

export interface BuilderFocusGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.Focus
  params: {
    center: number[]
    zoom?: number
  }
}

export interface BuilderFocusLayerGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.FocusLayer
  params: {
    layerId: string
  }
}

export interface BuilderGetFormulaWidgetGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.GetFormulaWidget
  params: {
    widgetId: string
  }
}

export interface BuilderGetCategoryWidgetGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.GetCategoryWidget
  params: {
    widgetId: string
  }
}

export interface BuilderGetGetHistogramWidgetGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.GetHistogramWidget
  params: {
    widgetId: string
  }
}

export interface BuilderGetGetTimeSeriesGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.GetTimeSeriesWidget
  params: {
    widgetId: string
    timeRange: [number, number]
  }
}

export interface BuilderFilterCategoryWidgetGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.FilterCategoryWidget
  params: {
    widgetId: string
    categories: string[]
  }
}

export interface BuilderFilterHistogramWidgetGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.FilterHistogramWidget
  params: {
    widgetId: string
    intervals: Array<Array<number>>
  }
}

export interface BuilderFilterRangeWidgetGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.FilterRangeWidget
  params: {
    widgetId: string
    range: number[]
  }
}

export interface BuilderSetLayersVisibilityGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.SetLayersVisibility
  params: {
    layers: Array<{ layerId: string; visibility: boolean; mapIndex?: 0 | 1 }>
  }
}

export interface BuilderSetSqlParameterTextGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.SetSqlParameterText
  params: {
    parameterId: string
    values: string[]
  }
}

export interface BuilderSetSqlParameterDateRangeGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.SetSqlParameterDateRange
  params: {
    parameterId: string
    min?: string
    max?: string
  }
}

export interface BuilderSetSqlParameterNumericGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.SetSqlParameterNumeric
  params: {
    parameterId: string
    value: number
  }
}

export interface BuilderSetSqlParameterNumericRangeGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.SetSqlParameterNumericRange
  params: {
    parameterId: string
    min?: number
    max?: number
  }
}

export interface BuilderAddSourceGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.AddSource
  params: {
    type: MapType
    source: string
    connection: string
    geoColumn?: string
  }
}

export interface BuilderRemoveSourceGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.RemoveSource
  params: { datasetId: string }
}

export interface BuilderAddLayerGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.AddLayer
  params: {
    datasetId: string
    label: string
    hoverColumns?: string[]
  }
}

export interface BuilderRemoveLayerGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.RemoveLayer
  params: { layerId: string }
}

export interface BuilderSetLayerStyleGenAIAction extends BuilderGenAIAction {
  action: BuilderGenAIActionTypes.SetLayerStyle
  params: {
    layerId: string
    opacity?: number
    fillColor?: string
    fillColorColumn?: string
    fillColorPalette?: string[]
    outlineWidth?: number
    outlineColor?: string
    radius?: number
    radiusColumn?: string
  }
}

export interface BuilderGenAIActionResult {
  id: string
  result: unknown
}

export type DynamicTilingMigrationBackup = {
  layers: KeplerStoredLayer[]
  popupSettings?: PopupSettings
  widgets?: Widget[]
  datasets: MapDataset[]
}
